import { useState, useEffect } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { ArrowLeftIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { fetchAnalysisById, deleteResume } from '../services/resumeService'
import { Skeleton } from './Skeleton'
import type { ResumeAnalysis } from '../types'

interface Props {
  userId: string
}

export function ResumeAnalysisDetailPage({ userId }: Props) {
  const { analysisId } = useParams<{ analysisId: string }>()
  const navigate = useNavigate()
  const [analysis, setAnalysis] = useState<ResumeAnalysis | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    if (!analysisId || !userId) return

    setLoading(true)
    setError(null)

    fetchAnalysisById(analysisId, userId)
      .then((data) => {
        if (!data) {
          setError('Analysis not found')
        } else {
          setAnalysis(data)
        }
      })
      .catch((err) => {
        console.error('Failed to fetch analysis:', err)
        setError('Failed to load analysis')
      })
      .finally(() => setLoading(false))
  }, [analysisId, userId])

  const handleDelete = async () => {
    if (!analysis || !analysis.file_path) return
    
    setIsDeleting(true)
    try {
      await deleteResume(analysis.file_path, userId)
      navigate('/resume')
    } catch (err) {
      console.error('Failed to delete resume:', err)
      setError('Failed to delete resume')
      setIsDeleting(false)
      setIsDeleteModalOpen(false)
    }
  }

  if (loading) {
    return (
      <div className="mx-auto max-w-6xl space-y-6">
        <Link
          to="/resume"
          className="flex items-center gap-1.5 text-sm text-app-muted hover:text-app-ink transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to Resume Analysis
        </Link>
        <div className="card p-6 space-y-6">
          {/* Header Skeleton */}
          <div className="border-b border-app-line pb-4 space-y-2">
            <Skeleton className="h-3 w-32" />
            <Skeleton className="h-8 w-96" />
            <Skeleton className="h-4 w-64" />
          </div>

          {/* Scores Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Skeleton className="h-32 w-full rounded-lg" />
            <Skeleton className="h-32 w-full rounded-lg" />
          </div>

          {/* Sections Skeleton */}
          <Skeleton className="h-48 w-full rounded-lg" />
          <Skeleton className="h-48 w-full rounded-lg" />
          <Skeleton className="h-48 w-full rounded-lg" />
          <Skeleton className="h-40 w-full rounded-lg" />
        </div>
      </div>
    )
  }

  if (error || !analysis) {
    return (
      <div className="mx-auto max-w-6xl space-y-6">
        <Link
          to="/resume"
          className="flex items-center gap-1.5 text-sm text-app-muted hover:text-app-ink transition-colors"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to Resume Analysis
        </Link>
        <div className="card p-6 space-y-6">
          <div className="border-b border-app-line pb-4">
            <p className="text-xs uppercase text-app-muted mb-1">Resume Analysis</p>
            <h1 className="text-2xl font-bold text-app-ink font-display">Analysis Not Found</h1>
          </div>
          <div className="rounded-lg border border-rose-200 dark:border-rose-900/50 bg-rose-50 dark:bg-rose-950/30 px-4 py-3">
            <p className="text-sm text-rose-700 dark:text-rose-200">{error || 'This analysis does not exist or you do not have permission to view it.'}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <Link
        to="/resume"
        className="flex items-center gap-1.5 text-sm text-app-muted hover:text-app-ink transition-colors"
      >
        <ArrowLeftIcon className="h-4 w-4" />
        Back to Resume Analysis
      </Link>
      <div className="card p-6 space-y-6">
          {/* Header */}
          <div className="border-b border-app-line pb-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs uppercase text-app-muted mb-1">Resume Analysis</p>
                <h1 className="text-2xl font-bold text-app-ink break-all sm:break-normal font-display">{analysis.file_name}</h1>
                <p className="text-sm text-app-muted mt-1">
                  {new Date(analysis.created_at).toLocaleDateString('pl-PL', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </p>
              </div>
              {analysis.file_path && (
                <button
                  onClick={() => setIsDeleteModalOpen(true)}
                  className="flex items-center gap-1.5 rounded-lg border border-rose-300 dark:border-rose-900/50 bg-rose-100 dark:bg-rose-950/30 px-3 py-2 text-sm font-medium text-rose-900 dark:text-rose-300 hover:bg-rose-200 dark:hover:bg-rose-900/30 transition-colors"
                >
                  <TrashIcon className="h-4 w-4" />
                  Delete
                </button>
              )}
            </div>
          </div>

          {/* Analysis Results */}
          <div className="space-y-6">
            {/* Header with scores */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border border-app-line bg-app-subtle p-6 rounded-2xl shadow-app-card">
                <p className="text-app-muted text-sm mb-2">Overall Score</p>
                <div className="flex items-end gap-2">
                  <span className="text-4xl font-bold text-app-ink">
                    {analysis.overall_score}
                  </span>
                  <span className="text-app-muted mb-1">/100</span>
                </div>
                <div className="mt-3 h-2 bg-app-subtle rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-emerald-500 to-sky-500 transition-all duration-500"
                    style={{ width: `${analysis.overall_score}%` }}
                  />
                </div>
              </div>

              <div className="border border-app-line bg-app-subtle p-6 rounded-2xl shadow-app-card">
                <div className="flex items-center gap-2 mb-2">
                  <p className="text-app-muted text-sm">ATS Compatibility</p>
                  <div className="group relative">
                    <svg className="w-4 h-4 text-app-muted cursor-help" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block w-64 p-2 bg-app-surface border border-app-line rounded-lg text-xs text-app-ink shadow-lg z-10">
                      <strong>ATS (Applicant Tracking System)</strong> - automated systems used by companies to filter resumes. A higher score means your resume is more likely to pass automated screening.
                    </div>
                  </div>
                </div>
                <div className="flex items-end gap-2">
                  <span className="text-4xl font-bold text-app-ink">
                    {analysis.ats_score}
                  </span>
                  <span className="text-app-muted mb-1">/100</span>
                </div>
                <div className="mt-3 h-2 bg-app-subtle rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-amber-500 to-emerald-500 transition-all duration-500"
                    style={{ width: `${analysis.ats_score}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Strengths */}
            <div className="border border-app-line bg-app-subtle p-6 rounded-2xl shadow-app-card">
              <h3 className="text-lg font-semibold text-emerald-700 dark:text-emerald-400 mb-4 flex items-center gap-2 font-display">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                Strengths
              </h3>
              <ul className="space-y-2">
                {analysis.strengths.map((strength, i) => (
                  <li key={i} className="flex items-start gap-2 text-app-ink">
                    <span className="text-emerald-700 dark:text-emerald-400 mt-1 flex-shrink-0">•</span>
                    <span>{strength}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Weaknesses */}
            <div className="border border-app-line bg-app-subtle p-6 rounded-2xl shadow-app-card">
              <h3 className="text-lg font-semibold text-red-700 dark:text-red-400 mb-4 flex items-center gap-2 font-display">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                Areas for Improvement
              </h3>
              <ul className="space-y-2">
                {analysis.weaknesses.map((weakness, i) => (
                  <li key={i} className="flex items-start gap-2 text-app-ink">
                    <span className="text-red-700 dark:text-red-400 mt-1 flex-shrink-0">•</span>
                    <span>{weakness}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Suggestions */}
            <div className="border border-app-line bg-app-subtle p-6 rounded-2xl shadow-app-card">
              <h3 className="text-lg font-semibold text-sky-700 dark:text-sky-400 mb-4 flex items-center gap-2 font-display">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                  />
                </svg>
                Actionable Suggestions
              </h3>
              <ul className="space-y-3">
                {analysis.suggestions.map((suggestion, i) => (
                  <li key={i} className="flex items-start gap-3 text-app-ink">
                    <span className="bg-sky-100 dark:bg-sky-500/20 text-sky-700 dark:text-sky-400 rounded-full w-6 h-6 flex items-center justify-center text-sm flex-shrink-0 mt-0.5 font-semibold">
                      {i + 1}
                    </span>
                    <span>{suggestion}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Missing Keywords */}
            {analysis.missing_keywords && analysis.missing_keywords.length > 0 && (
              <div className="border border-app-line bg-app-subtle p-6 rounded-2xl shadow-app-card">
                <h3 className="text-lg font-semibold text-amber-800 dark:text-amber-400 mb-4 font-display">
                  Missing Keywords
                </h3>
                <div className="flex flex-wrap gap-2">
                  {analysis.missing_keywords.map((keyword, i) => (
                    <span
                      key={i}
                      className="px-3 py-1 bg-amber-100 dark:bg-amber-500/20 text-amber-800 dark:text-amber-400 rounded-full text-sm border border-amber-300 dark:border-amber-500/30"
                    >
                      {keyword}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Formatting Issues */}
            {analysis.formatting_issues && analysis.formatting_issues.length > 0 && (
              <div className="border border-app-line bg-app-subtle p-6 rounded-2xl shadow-app-card">
                <h3 className="text-lg font-semibold text-app-ink mb-4 font-display">
                  Formatting Issues
                </h3>
                <ul className="space-y-2">
                  {analysis.formatting_issues.map((issue, i) => (
                    <li key={i} className="flex items-start gap-2 text-app-ink">
                      <span className="text-app-muted mt-1 flex-shrink-0">•</span>
                      <span>{issue}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
      </div>

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div className="w-full max-w-md border border-app-line bg-app-surface p-4 space-y-4 rounded-2xl shadow-app-card">
            <div className="flex items-center justify-between">
              <p className="text-base font-semibold text-rose-500 flex items-center gap-2">
                <TrashIcon className="h-5 w-5" /> Delete Resume
              </p>
              <button
                onClick={() => setIsDeleteModalOpen(false)}
                className="p-1 text-app-muted hover:bg-app-subtle rounded-lg"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-3">
              <p className="text-sm text-app-ink">
                Are you sure you want to delete this resume and its analysis? This action cannot be undone.
              </p>
              <p className="text-sm text-app-muted">
                File: <strong>{analysis.file_name}</strong>
              </p>
              <div className="rounded-lg border border-amber-300 dark:border-amber-900/50 bg-amber-50 dark:bg-amber-950/30 p-3">
                <p className="text-sm text-amber-800 dark:text-amber-300">
                  <strong>Note:</strong> If you delete this resume, you will need to upload a new one before using the Resume Match feature.
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setIsDeleteModalOpen(false)}
                disabled={isDeleting}
                className="rounded-lg border border-app-line px-4 py-2 text-sm font-medium text-app-ink hover:bg-app-subtle disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="flex items-center gap-2 rounded-lg bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50"
              >
                {isDeleting && (
                  <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                )}
                Delete Resume
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}


